import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageSquare, Globe2, ShieldCheck, Loader2 } from "lucide-react";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Collecting payments",
  "Sending payouts",
  "Multi-currency accounts",
  "FX & currency conversion",
  "Payment links",
  "API integration",
  "Something else",
];

const inputCls =
  "w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors focus:border-[#2E7CD6] focus:ring-2 focus:ring-[#2E7CD6]/15";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-[#0F172A]">
      {label}
    </label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", country: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — the Swiftwire team will be in touch shortly.");
      setForm({ first_name: "", last_name: "", email: "", company: "", country: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="sw-grid-bg bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:gap-20 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Contact</p>
            <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
              Talk to Swiftwire.
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#334155] md:text-lg">
              Tell us what you're building and we'll show you how Swiftwire can help.
            </p>
            <ul className="mt-10 space-y-5">
              {[
                { icon: MessageSquare, text: "A conversation about your money movement needs — not a sales script." },
                { icon: Globe2, text: "Guidance on collections, payouts, accounts, FX and API integration." },
                { icon: ShieldCheck, text: "Your details stay with the Swiftwire team. No spam, ever." },
              ].map((x, i) => (
                <li key={i} className="flex items-start gap-3.5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EFF5FD] text-[#1E3A6E]">
                    <x.icon size={16} strokeWidth={1.5} />
                  </span>
                  <p className="text-sm leading-relaxed text-[#334155]">{x.text}</p>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-[#E2E8F0] bg-white p-7 shadow-xl shadow-slate-900/[0.05] sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="First Name" htmlFor="first_name">
                  <input id="first_name" data-testid="contact-first-name" required value={form.first_name} onChange={update("first_name")} className={inputCls} placeholder="Amara" />
                </Field>
                <Field label="Last Name" htmlFor="last_name">
                  <input id="last_name" data-testid="contact-last-name" required value={form.last_name} onChange={update("last_name")} className={inputCls} placeholder="Okafor" />
                </Field>
                <Field label="Work Email" htmlFor="email">
                  <input id="email" type="email" data-testid="contact-email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
                </Field>
                <Field label="Company" htmlFor="company">
                  <input id="company" data-testid="contact-company" required value={form.company} onChange={update("company")} className={inputCls} placeholder="Company Ltd" />
                </Field>
                <Field label="Country" htmlFor="country">
                  <input id="country" data-testid="contact-country" required value={form.country} onChange={update("country")} className={inputCls} placeholder="United Kingdom" />
                </Field>
                <Field label="What are you looking to do?" htmlFor="interest">
                  <select id="interest" data-testid="contact-interest" required value={form.interest} onChange={update("interest")} className={inputCls}>
                    <option value="" disabled>Select an option</option>
                    {interests.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Message" htmlFor="message">
                  <textarea id="message" data-testid="contact-message" rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="Tell us a little about what you're building…" />
                </Field>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A6E] px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#16294e] disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
                {loading ? "Sending…" : "Contact Swiftwire"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
